'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { formatName } from '@pickleballinc/lib/format-name';
import { Checkbox } from '@pickleballinc/ui/components/ui/checkbox';
import { useForm } from 'react-hook-form';

import { ActionButtons } from '@/components/molecules';
import { ApproveDeclineTradeData, approveDeclineTradeSchema } from '@/entities';

interface ApproveDeclineTradeFormProps {
	tradeTo: { firstName: string; lastName: string };
	tradeFor: { firstName: string; lastName: string };
	teamName: string;
	onSubmit: (data: ApproveDeclineTradeData) => void;
	onClose: () => void;
}

export const ApproveDeclineTradeForm = ({ tradeTo, tradeFor, onSubmit, onClose, teamName }: ApproveDeclineTradeFormProps) => {
	const form = useForm<ApproveDeclineTradeData>({
		resolver: zodResolver(approveDeclineTradeSchema),
		defaultValues: {
			approveTrade: false,
			declineTrade: false
		}
	});

	const { handleSubmit, setValue, watch } = form;
	const isApproveChecked = watch('approveTrade');
	const isDeclineChecked = watch('declineTrade');

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<p className="pb-4 text-lg font-semibold text-gray-500">
				{`${teamName} is offering to trade ${formatName({ firstName: tradeTo.firstName, lastName: tradeTo.lastName })} for ${formatName({ firstName: tradeFor.firstName, lastName: tradeFor.lastName })}`}
			</p>

			<div className="flex items-center">
				<Checkbox
					className="size-5 cursor-pointer rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
					name="approveTrade"
					checked={isApproveChecked}
					onCheckedChange={(checked: boolean) => setValue('approveTrade', checked)}
				/>
				<p className="pl-2">Approve this trade offer</p>
			</div>
			<div className="flex items-center pt-4">
				<Checkbox
					className=" size-5 cursor-pointer rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
					name="declineTrade"
					checked={isDeclineChecked}
					onCheckedChange={(checked: boolean) => setValue('declineTrade', checked)}
				/>
				<p className="pl-2">Decline this trade offer</p>
			</div>

			<ActionButtons
				className="flex place-self-end !px-[10px] !py-[16px]"
				closeButtonClassName="w-full"
				submitButtonClassName="w-full"
				onClose={onClose}
				submitButtonText="Send"
				closeButtonText="Cancel"
				submitButtonDisabled={!isApproveChecked && !isDeclineChecked}
			/>
		</form>
	);
};
